import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';

// Components
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';
 import TestAddProperty from './components/test-add-property';
   
// Hooks
import { useAuth } from './hooks/useAuth';
import { useGlobalErrorHandler } from './hooks/useGlobalErrorHandler';

// Types
import { ProtectedRouteProps } from './types/auth';

// Lazy loaded pages
const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const PropertyDetails = lazy(() => import('./pages/PropertyDetails'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const PropertyManagement = lazy(() => import('./pages/PropertyManagement'));
const AddProperty = lazy(() => import('./pages/AddProperty'));
const ClientManagement = lazy(() => import('./pages/ClientManagement'));
const MyRecommendations = lazy(() => import('./pages/MyRecommendations'));
const PropertyCatalog = lazy(() => import('./pages/PropertyCatalog'));
const Profile = lazy(() => import('./pages/Profile'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Protected Route Component
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  allowedRoles = [], 
  redirectTo = '/login' 
}) => {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  if (allowedRoles.length > 0 && user && !allowedRoles.includes(user.tipo)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Page Transition Component
const PageTransition: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.3, ease: 'easeInOut' }}
  >
    {children}
  </motion.div>
);

const App: React.FC = () => {
  // Inicializar captura de erros globais
  useGlobalErrorHandler();

  return (
    <>
      <Helmet>
        <title>Imobiliária Premium - Propriedades Exclusivas</title>
        <meta name="description" content="Há mais de 15 anos no mercado imobiliário, somos referência em qualidade e confiança. Propriedades exclusivas para investidores e corretores experientes." />
        <meta name="keywords" content="imóveis, catálogo, propriedades, investimento, imobiliária, premium" />
        <meta name="author" content="Imobiliária Premium" />
        <meta property="og:title" content="Imobiliária Premium" />
        <meta property="og:description" content="Propriedades exclusivas para investidores experientes" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.origin} />
        <link rel="canonical" href={window.location.origin} />
      </Helmet>

      <ErrorBoundary>
        <Layout>
          <AnimatePresence mode="wait">
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                {/* Public Routes */}
                <Route 
                  path="/" 
                  element={
                    <PageTransition>
                      <Home />
                    </PageTransition>
                  } 
                />
                
                <Route 
                  path="/login" 
                  element={
                    <PageTransition>
                      <Login />
                    </PageTransition>
                  } 
                />
                
                <Route 
                  path="/register" 
                  element={
                    <PageTransition>
                      <Register />
                    </PageTransition>
                  } 
                />
                
                <Route 
                  path="/property/:id" 
                  element={
                    <PageTransition>
                      <PropertyDetails />
                    </PageTransition>
                  } 
                />
                <Route path="/test-property" element={<TestAddProperty />} />
                {/* Protected Routes - Cliente */}
                <Route 
                  path="/my-recommendations" 
                  element={
                    <ProtectedRoute allowedRoles={['cliente']}>
                      <PageTransition>
                        <MyRecommendations />
                      </PageTransition>
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/catalog" 
                  element={
                    <ProtectedRoute allowedRoles={['cliente']}>
                      <PageTransition>
                        <PropertyCatalog />
                      </PageTransition>
                    </ProtectedRoute>
                  } 
                />

                {/* Protected Routes - Concessionária */}
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute allowedRoles={['concessionaria']}>
                      <PageTransition>
                        <Dashboard />
                      </PageTransition>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/properties" 
                  element={
                    <ProtectedRoute allowedRoles={['concessionaria']}>
                      <PageTransition>
                        <PropertyManagement />
                      </PageTransition>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/properties/add" 
                  element={
                    <ProtectedRoute allowedRoles={['concessionaria']}>
                      <PageTransition>
                        <AddProperty />
                      </PageTransition>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/clients" 
                  element={
                    <ProtectedRoute allowedRoles={['concessionaria']}>
                      <PageTransition>
                        <ClientManagement />
                      </PageTransition>
                    </ProtectedRoute>
                  } 
                />

                {/* Protected Routes - All Users */}
                <Route 
                  path="/profile" 
                  element={
                    <ProtectedRoute>
                      <PageTransition>
                        <Profile />
                      </PageTransition>
                    </ProtectedRoute>
                  } 
                />

                {/* 404 Route */}
                <Route 
                  path="*" 
                  element={
                    <PageTransition>
                      <NotFound />
                    </PageTransition>
                  } 
                />
              </Routes>
            </Suspense>
          </AnimatePresence>
        </Layout>
      </ErrorBoundary>
    </>
  );
};

export default App;
